import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import api from '../services/api';
import './FilaOrdens.css';
import { useDataRefresher } from '../hooks/useDataRefresher';
import {
  FaArrowLeft, FaFilter, FaPlus, FaTrash, FaExternalLinkAlt,
  FaRegClock, FaCheckCircle, FaTimesCircle, FaSync, FaRedo,
  FaExclamationTriangle, FaBolt, FaFileExport   // ← FaBolt + FaFileExport
} from 'react-icons/fa';
import { MdOutlineLocalShipping, MdOutlineAssignment, MdOutlineBusiness } from 'react-icons/md';
import CadastroOrdensModal    from './CadastroOrdensModal';
import ModalAutoDistribuicao  from './ModalAutoDistribuicao'; // ← import novo
import RelatorioOrdens        from './RelatorioOrdens';        // ← import novo

// ─────────────────────────────────────────────
// Helpers
// ─────────────────────────────────────────────
const normalize = (str) =>
  str?.toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '').trim() || '';

const getCurrentMonth = () => {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
};

const normalizarMotoristas = (lista = []) => {
  const normalizados = lista.map((m) => ({
    ...m,
    id_raw: m.id,
    id: parseInt(String(m.id).replace('MOT-', '').replace('USR-', ''), 10),
    origem: m.origem || 'motorista',
  }));

  const seen = new Map();
  for (const m of normalizados) {
    const chave = normalize(m.nome);
    if (!seen.has(chave) || m.origem === 'operacional') {
      seen.set(chave, m);
    }
  }
  return Array.from(seen.values());
};

// ─────────────────────────────────────────────
// Ícones
// ─────────────────────────────────────────────
const ClipIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24"
    fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M21.44 11.05l-9.19 9.19a6 6 0 0 1-8.49-8.49l9.19-9.19a4 4 0 0 1 5.66 5.66l-9.2 9.19a2 2 0 0 1-2.83-2.83l8.49-8.48" />
  </svg>
);

// ─────────────────────────────────────────────
// Dropdown customizado de motorista
// ─────────────────────────────────────────────
const MotoristaSelect = ({ motoristas, value, onChange, currentMotoristaId, disabled }) => {
  const [open, setOpen]     = useState(false);
  const [search, setSearch] = useState('');
  const containerRef        = useRef(null);
  const searchRef           = useRef(null);

  const selected = motoristas.find(m => m.id === value);

  const opcoes = useMemo(() =>
    motoristas
      .filter(m => m.id !== currentMotoristaId)
      .filter(m => !search || normalize(m.nome).includes(normalize(search))),
    [motoristas, currentMotoristaId, search]
  );

  useEffect(() => {
    const handler = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setOpen(false);
        setSearch('');
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  useEffect(() => {
    if (open && searchRef.current) searchRef.current.focus();
  }, [open]);

  const handleSelect = (m) => {
    onChange(m.id);
    setOpen(false);
    setSearch('');
  };

  return (
    <div ref={containerRef} style={{ position: 'relative', width: '100%' }}>
      <button
        type="button"
        disabled={disabled}
        onClick={() => !disabled && setOpen(o => !o)}
        style={{
          width: '100%',
          padding: '10px 36px 10px 12px',
          border: `1px solid ${open ? '#3b82f6' : '#e2e8f0'}`,
          borderRadius: '8px',
          background: disabled ? '#f8fafc' : '#ffffff',
          fontSize: '0.85rem',
          fontFamily: 'inherit',
          fontWeight: selected ? 600 : 400,
          color: selected ? '#0f172a' : '#94a3b8',
          textAlign: 'left',
          cursor: disabled ? 'not-allowed' : 'pointer',
          boxShadow: open ? '0 0 0 3px rgba(59,130,246,.12)' : 'none',
          transition: 'border-color .12s, box-shadow .12s',
          position: 'relative',
        }}
      >
        {selected
          ? <>
              {selected.nome}
              {selected.origem === 'operacional' && (
                <span style={{ marginLeft: 6, fontSize: '0.7rem', color: '#64748b', fontWeight: 500 }}>
                  (Operacional)
                </span>
              )}
            </>
          : '— Selecione o motorista —'}
        <svg
          viewBox="0 0 20 20" fill="none" width="16" height="16"
          style={{ position: 'absolute', right: 10, top: '50%', transform: `translateY(-50%) rotate(${open ? 180 : 0}deg)`, transition: 'transform .15s', color: '#94a3b8' }}
        >
          <path d="M5 7.5L10 12.5L15 7.5" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
      </button>

      {open && (
        <div style={{
          position: 'absolute',
          top: 'calc(100% + 4px)',
          left: 0,
          right: 0,
          zIndex: 99999,
          background: '#ffffff',
          border: '1px solid #e2e8f0',
          borderRadius: '10px',
          boxShadow: '0 10px 30px rgba(15,23,42,.14)',
          overflow: 'hidden',
        }}>
          <div style={{ padding: '8px 8px 4px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: '7px', padding: '6px 10px' }}>
              <svg viewBox="0 0 20 20" fill="none" width="13" height="13" style={{ color: '#94a3b8', flexShrink: 0 }}>
                <circle cx="8.5" cy="8.5" r="5.5" stroke="currentColor" strokeWidth="1.6"/>
                <path d="M13.5 13.5L17 17" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round"/>
              </svg>
              <input
                ref={searchRef}
                type="text"
                placeholder="Buscar motorista..."
                value={search}
                onChange={e => setSearch(e.target.value)}
                onKeyDown={e => e.key === 'Escape' && setOpen(false)}
                style={{ border: 'none', outline: 'none', background: 'transparent', fontSize: '0.82rem', fontFamily: 'inherit', color: '#0f172a', width: '100%' }}
              />
              {search && (
                <button type="button" onClick={() => setSearch('')}
                  style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#94a3b8', padding: 0, lineHeight: 1 }}>
                  ✕
                </button>
              )}
            </div>
          </div>
          <div style={{ maxHeight: 220, overflowY: 'auto' }}>
            {opcoes.length === 0 ? (
              <div style={{ padding: '16px', textAlign: 'center', color: '#94a3b8', fontSize: '0.82rem' }}>
                Nenhum motorista encontrado
              </div>
            ) : (
              opcoes.map(m => (
                <div
                  key={m.id_raw}
                  onMouseDown={(e) => { e.preventDefault(); handleSelect(m); }}
                  style={{
                    padding: '10px 14px',
                    cursor: 'pointer',
                    fontSize: '0.85rem',
                    fontWeight: m.id === value ? 700 : 500,
                    color: m.id === value ? '#3b82f6' : '#1e293b',
                    background: m.id === value ? '#eff6ff' : 'transparent',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    transition: 'background .08s',
                  }}
                  onMouseEnter={e => { if (m.id !== value) e.currentTarget.style.background = '#f8fafc'; }}
                  onMouseLeave={e => { if (m.id !== value) e.currentTarget.style.background = 'transparent'; }}
                >
                  <span>{m.nome}</span>
                  <span style={{ fontSize: '0.7rem', color: m.origem === 'operacional' ? '#64748b' : '#94a3b8', fontWeight: 500 }}>
                    {m.origem === 'operacional' ? 'Operacional' : 'Motorista'}
                  </span>
                </div>
              ))
            )}
          </div>
          <div style={{ padding: '6px 14px', borderTop: '1px solid #f1f5f9', fontSize: '0.7rem', color: '#94a3b8', textAlign: 'right' }}>
            {opcoes.length} disponível{opcoes.length !== 1 ? 'is' : ''}
          </div>
        </div>
      )}
    </div>
  );
};

// ─────────────────────────────────────────────
// Timer
// ─────────────────────────────────────────────
const TimerDisplay = React.memo(({ status, data_criacao, compact = false }) => {
  const [timer, setTimer] = useState({ text: '', color: '#94a3b8' });

  useEffect(() => {
    const s = normalize(status);
    if (['concluido', 'finalizada', 'cancelado', 'lancamento excluido'].includes(s)) {
      setTimer({ text: 'Finalizado', color: '#94a3b8' });
      return;
    }
    if (!data_criacao) return;

    const fmt = (ms) => {
      const t = Math.floor(Math.abs(ms) / 1000);
      const h = String(Math.floor(t / 3600)).padStart(2, '0');
      const m = String(Math.floor((t % 3600) / 60)).padStart(2, '0');
      const sec = String(t % 60).padStart(2, '0');
      return `${h}:${m}:${sec}`;
    };

    const tick = () => {
      const diff = new Date(data_criacao).getTime() - Date.now();
      if (diff > 0)
        setTimer({ text: compact ? fmt(diff) : `Faltam ${fmt(diff)}`, color: '#3b82f6' });
      else if (s === 'em andamento')
        setTimer({ text: compact ? fmt(diff) : `Em andamento ${fmt(diff)}`, color: '#0ea5e9' });
      else
        setTimer({ text: compact ? fmt(diff) : `Atrasado ${fmt(diff)}`, color: '#ef4444' });
    };

    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [status, data_criacao, compact]);

  if (compact)
    return <span className="os-timer-compact" style={{ color: timer.color }}>{timer.text}</span>;

  return (
    <div className="os-timer" style={{ color: timer.color }}>
      <span className="timer-dot" style={{ background: timer.color }} />
      {timer.text}
    </div>
  );
});

// ─────────────────────────────────────────────
// Badges
// ─────────────────────────────────────────────
const STATUS_META = {
  'Na Fila':             { color: '#3b82f6', bg: '#eff6ff', icon: '⏳' },
  'Agendado':            { color: '#f59e0b', bg: '#fffbeb', icon: '📅' },
  'Em Andamento':        { color: '#0ea5e9', bg: '#f0f9ff', icon: '🔄' },
  'Concluído':           { color: '#10b981', bg: '#f0fdf4', icon: '✅' },
  'Finalizada':          { color: '#10b981', bg: '#f0fdf4', icon: '✅' },
  'Cancelado':           { color: '#ef4444', bg: '#fef2f2', icon: '✕' },
  'Lançamento Excluído': { color: '#94a3b8', bg: '#f8fafc', icon: '—' },
};

const StatusBadge = ({ status }) => {
  const safeStatus = status?.trim() || 'Na Fila';
  const meta = STATUS_META[safeStatus] || STATUS_META['Na Fila'];
  return (
    <span className="badge-status" style={{ color: meta.color, background: meta.bg, border: `1px solid ${meta.color}22` }}>
      {meta.icon} {safeStatus === 'Finalizada' ? 'Concluído (App)' : safeStatus}
    </span>
  );
};

const OrigemBadge = ({ origem }) => {
  if (origem === 'seguradora')
    return <span className="badge-origem seguradora"><MdOutlineBusiness size={11} /> Seguradora</span>;
  if (origem === 'app')
    return <span className="badge-origem app"><MdOutlineLocalShipping size={11} /> Via App</span>;
  return <span className="badge-origem interno"><MdOutlineAssignment size={11} /> Interno</span>;
};

const IntegracaoStatusBadge = ({ statusIntegracao }) => {
  const mapping = {
    'os_criada':        { text: 'Aguardando Atendimento', color: '#64748b', bg: '#f1f5f9' },
    'retorno_pendente': { text: 'Retorno Pendente',        color: '#f59e0b', bg: '#fffbeb' },
    'retorno_enviado':  { text: 'Retorno Enviado',         color: '#10b981', bg: '#f0fdf4' },
    'retorno_erro':     { text: 'Falha no Retorno',        color: '#ef4444', bg: '#fef2f2' },
  };
  const meta = mapping[statusIntegracao] || { text: statusIntegracao || 'Desconhecido', color: '#64748b', bg: '#f1f5f9' };
  return (
    <span style={{ background: meta.bg, color: meta.color, padding: '3px 8px', borderRadius: '4px', fontSize: '0.7rem', fontWeight: 'bold' }}>
      {meta.text}
    </span>
  );
};

// ─────────────────────────────────────────────
// Photo Gallery
// ─────────────────────────────────────────────
const SIDES = [
  { key: 'front', label: 'Frente',        emoji: '🔵' },
  { key: 'rear',  label: 'Traseira',      emoji: '🔴' },
  { key: 'left',  label: 'Lado Esquerdo', emoji: '🟡' },
  { key: 'right', label: 'Lado Direito',  emoji: '🟢' },
];

const PhotoGallery = ({ anexos }) => {
  const [selected, setSelected] = useState(null);
  const [imageErrors, setImageErrors] = useState({});

  const images = useMemo(() =>
    (anexos || []).filter(a => (a.mime_type || '').startsWith('image/')), [anexos]);

  const sideMap = useMemo(() => {
    const m = {};
    SIDES.forEach(s => {
      m[s.key] = images.find(a => (a.nome_arquivo || '').includes(`required_${s.key}`));
    });
    return m;
  }, [images]);

  const extras = useMemo(() =>
    images.filter(a => !SIDES.some(s => (a.nome_arquivo || '').includes(`required_${s.key}`))),
    [images]);

  const thumb = (driveId) => `https://drive.google.com/uc?export=view&id=${driveId}`;
  const handleImageError = (id) => setImageErrors(prev => ({ ...prev, [id]: true }));

  if (images.length === 0)
    return (
      <div className="photo-empty">
        <span>📷</span>
        <p>Nenhuma evidência fotográfica registrada</p>
      </div>
    );

  return (
    <>
      {selected && (
        <div className="lightbox-overlay" onClick={() => setSelected(null)}>
          <div className="lightbox-inner" onClick={e => e.stopPropagation()}>
            <button className="lightbox-close" onClick={() => setSelected(null)}>✕</button>
            {imageErrors[selected.drive_file_id] ? (
              <div className="photo-empty" style={{ width: '100%', minHeight: '300px', borderRadius: '12px' }}>
                <span>🔒</span>
                <p>Imagem privada no Drive.</p>
                <a href={selected.web_view_link} target="_blank" rel="noreferrer" style={{ marginTop: '10px', color: '#3b82f6' }}>
                  Clique aqui para abrir
                </a>
              </div>
            ) : (
              <img src={thumb(selected.drive_file_id)} alt={selected.nome_arquivo}
                onError={() => handleImageError(selected.drive_file_id)} />
            )}
            <div className="lightbox-footer">
              <span>{selected.nome_arquivo}</span>
              <a href={selected.web_view_link} target="_blank" rel="noreferrer">
                <FaExternalLinkAlt size={12} /> Abrir no Drive
              </a>
            </div>
          </div>
        </div>
      )}

      <div className="photo-grid-2x2">
        {SIDES.map(side => {
          const photo = sideMap[side.key];
          return (
            <div key={side.key} className={`photo-slot ${photo ? 'filled' : 'empty'}`}
              onClick={() => photo && setSelected(photo)}>
              {photo ? (
                <>
                  {imageErrors[photo.drive_file_id] ? (
                    <div className="photo-slot-empty" style={{ background: '#f1f5f9' }}>
                      <span>🔒</span>
                      <small style={{ color: '#64748b' }}>Acesso Drive</small>
                    </div>
                  ) : (
                    <img src={thumb(photo.drive_file_id)} alt={side.label}
                      onError={() => handleImageError(photo.drive_file_id)} />
                  )}
                  <div className="photo-slot-label">{side.label}</div>
                  <div className="photo-slot-overlay"><FaExternalLinkAlt size={14} /></div>
                </>
              ) : (
                <div className="photo-slot-empty">
                  <span>{side.emoji}</span>
                  <span>{side.label}</span>
                  <small>Não enviada</small>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {extras.length > 0 && (
        <div className="photo-extras">
          <p className="photo-extras-title">Anexos extras ({extras.length})</p>
          <div className="photo-extras-strip">
            {extras.map(a => (
              <div key={a.id} className="photo-extra-thumb" onClick={() => setSelected(a)}>
                {imageErrors[a.drive_file_id] ? (
                  <div style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#f1f5f9' }}>🔒</div>
                ) : (
                  <img src={thumb(a.drive_file_id)} alt={a.nome_arquivo}
                    onError={() => handleImageError(a.drive_file_id)} />
                )}
              </div>
            ))}
          </div>
        </div>
      )}
    </>
  );
};

// ─────────────────────────────────────────────
// Checklist
// ─────────────────────────────────────────────
const ChecklistDisplay = ({ anexos }) => {
  const txtAnexo = (anexos || []).find(a => (a.nome_arquivo || '').includes('checklist_OS'));
  if (!txtAnexo) return null;
  return (
    <div className="checklist-badge">
      <span className="checklist-icon">📋</span>
      <span>Checklist submetido</span>
      <a href={txtAnexo.web_view_link} target="_blank" rel="noreferrer" className="checklist-link">
        Ver <FaExternalLinkAlt size={10} />
      </a>
    </div>
  );
};

// ─────────────────────────────────────────────
// OS Card
// ─────────────────────────────────────────────
const OSCard = React.memo(({ os, clientes, motoristas, user, onOpen, onDelete }) => {
  const clienteNome   = clientes.find(c => c.id === os.cliente_id)?.nome || 'N/A';
  const motoristaNome = motoristas.find(m => m.id === os.motorista_id)?.nome || '—';
  const safeStatus    = os.status?.trim() || 'Na Fila';
  const meta          = STATUS_META[safeStatus] || STATUS_META['Na Fila'];
  const isAdmin       = user?.perfil === 'admin_geral' || user?.perfil === 'admin';
  const origem        = os.origem || (safeStatus === 'Finalizada' ? 'app' : 'interno');

  return (
    <div className="os-card" style={{ '--status-color': meta.color }} onClick={() => onOpen(os)}>
      <div className="os-card-accent" />
      <div className="os-card-body">
        <div className="os-card-header">
          <div className="os-card-id">
            <span className="os-num">#{os.id}</span>
            <OrigemBadge origem={origem} />
            <StatusBadge status={safeStatus} />
          </div>
          <TimerDisplay status={safeStatus} data_criacao={os.data_criacao} compact />
        </div>

        <div className="os-card-info">
          <span title="Cliente">👤 {clienteNome}</span>
          <span className="os-card-separator">·</span>
          <span title="Local" className="os-card-local">📍 {os.local_atendimento}</span>
          {motoristaNome !== '—' && (
            <>
              <span className="os-card-separator">·</span>
              <span title="Motorista">🚛 {motoristaNome}</span>
            </>
          )}
        </div>

        <div className="os-card-footer">
          <span className="os-card-date">
            {os.data_criacao
              ? new Date(os.data_criacao).toLocaleString('pt-BR', { day: '2-digit', month: '2-digit', year: '2-digit', hour: '2-digit', minute: '2-digit' })
              : '--/--/----'}
          </span>
          {os.valor > 0 && <span className="os-card-value">R$ {Number(os.valor).toFixed(2)}</span>}
        </div>
      </div>

      {isAdmin && (
        <button className="os-card-delete" title="Excluir OS"
          onClick={e => { e.stopPropagation(); onDelete(os.id); }}>
          <FaTrash size={12} />
        </button>
      )}
    </div>
  );
});

// ─────────────────────────────────────────────
// Componente Principal
// ─────────────────────────────────────────────
export default function FilaOrdens({ user }) {
  const navigate = useNavigate();
  const { refreshKey, refreshData } = useDataRefresher();

  const [ordens,     setOrdens]     = useState([]);
  const [clientes,   setClientes]   = useState([]);
  const [motoristas, setMotoristas] = useState([]);
  const [veiculos,   setVeiculos]   = useState([]);

  const [selectedTab,  setSelectedTab]  = useState('Na Fila');
  const [searchQuery,  setSearchQuery]  = useState('');
  const [monthFilter,  setMonthFilter]  = useState(getCurrentMonth);
  const [page,         setPage]         = useState(1);
  const PAGE_SIZE = 20;

  const [isOSModalOpen,         setIsOSModalOpen]         = useState(false);
  const [isFilterModalOpen,     setIsFilterModalOpen]     = useState(false);
  const [isRescheduleModalOpen, setIsRescheduleModalOpen] = useState(false);
  const [isCadastroModalOpen,   setIsCadastroModalOpen]   = useState(false);

  // ← NOVO: controla o modal de distribuição automática
  const [showAutoDistModal, setShowAutoDistModal] = useState(false);
  const [showRelatorioModal, setShowRelatorioModal] = useState(false); // ← novo

  const [isReatribuirModalOpen,   setIsReatribuirModalOpen]   = useState(false);
  const [reatribuirMotoristaId,   setReatribuirMotoristaId]   = useState(null);
  const [reatribuirJustificativa, setReatribuirJustificativa] = useState('');
  const [isReatribuindo,          setIsReatribuindo]          = useState(false);

  const [searchScope, setSearchScope] = useState('tab');

  const [selectedOS,    setSelectedOS]    = useState(null);
  const [notas,         setNotas]         = useState([]);
  const [anexos,        setAnexos]        = useState([]);
  const [loadingAnexos, setLoadingAnexos] = useState(false);
  const [novaNota,      setNovaNota]      = useState('');

  const [integracaoData,    setIntegracaoData]    = useState(null);
  const [loadingIntegracao, setLoadingIntegracao] = useState(false);
  const [enviandoRetorno,   setEnviandoRetorno]   = useState(false);

  const [filterData, setFilterData] = useState({
    motorista_id: '', origem: '', data_criacao: '', data_resolucao: '',
  });
  const [rescheduleDateTime, setRescheduleDateTime] = useState('');
  const [isRefreshing,       setIsRefreshing]       = useState(false);

  const fileInputRef = useRef(null);

  const statusOptions = ['Na Fila', 'Agendado', 'Em Andamento', 'Concluído', 'Cancelado', 'Lançamento Excluído'];
  const isAdminUser   = user?.perfil === 'admin_geral' || user?.perfil === 'admin';

  const getAuthConfig = useCallback(() => {
    const token = localStorage.getItem('token');
    return { headers: { Authorization: `Bearer ${token}` } };
  }, []);

  const getYYYYMM = (d) => {
    if (!d) return '';
    const dt = new Date(d);
    return `${dt.getFullYear()}-${String(dt.getMonth() + 1).padStart(2, '0')}`;
  };

  const formatMonthLabel = (yyyyMM) => {
    if (!yyyyMM) return 'Todos os meses';
    const [y, m] = yyyyMM.split('-');
    return `${m}/${y}`;
  };

  // ── Data fetching ────────────────────────────
  const fetchOrdens = useCallback(async () => {
    try {
      const r = await api.get('/api/ordens', getAuthConfig());
      setOrdens(r.data || []);
    } catch (e) { console.error(e); }
  }, [getAuthConfig]);

  const fetchNotas = useCallback(async (os_id) => {
    if (!os_id) return;
    try {
      const r = await api.get(`/api/ordens/${os_id}/notas`, getAuthConfig());
      setNotas(r.data || []);
    } catch { setNotas([]); }
  }, [getAuthConfig]);

  const fetchAnexos = useCallback(async (os_id) => {
    if (!os_id) return;
    setLoadingAnexos(true);
    try {
      const r = await api.get(`/api/ordens/${os_id}/anexos`, getAuthConfig());
      setAnexos(r.data || []);
    } catch { setAnexos([]); }
    finally { setLoadingAnexos(false); }
  }, [getAuthConfig]);

  const fetchIntegracao = useCallback(async (os_id) => {
    if (!os_id) return;
    setLoadingIntegracao(true);
    try {
      const r = await api.get(`/api/ordens/${os_id}/integracao`, getAuthConfig());
      setIntegracaoData(r.data || null);
    } catch {
      setIntegracaoData(null);
    } finally {
      setLoadingIntegracao(false);
    }
  }, [getAuthConfig]);

  // ── Init ────────────────────────────────────
  useEffect(() => {
    const init = async () => {
      try {
        const cfg = getAuthConfig();
        const [o, c, m, v] = await Promise.all([
          api.get('/api/ordens',   cfg),
          api.get('/api/clients?limit=1000',  cfg),
          api.get('/api/drivers?limit=1000', cfg),
          api.get('/api/vehicles?limit=1000', cfg),
        ]);
        setOrdens(o.data || []);
        setClientes(c.data.data || []);
        setMotoristas(normalizarMotoristas(m.data.data || []));
        setVeiculos(v.data.data || []);
      } catch (e) { console.error(e); setOrdens([]); }
    };
    init();
  }, [refreshKey, getAuthConfig]);

  useEffect(() => {
    const id = setInterval(fetchOrdens, 60000);
    return () => clearInterval(id);
  }, [fetchOrdens]);

  const handleManualRefresh = async () => {
    setIsRefreshing(true);
    await fetchOrdens();
    if (refreshData) refreshData();
    setTimeout(() => setIsRefreshing(false), 600);
  };

  // ── Handlers ────────────────────────────────
  const handleOpenOSModal = useCallback((os) => {
    setSelectedOS(os);
    fetchNotas(os.id);
    fetchAnexos(os.id);
    if (os.origem === 'seguradora' || os.seguradora_id) {
      fetchIntegracao(os.id);
    } else {
      setIntegracaoData(null);
    }
    setIsOSModalOpen(true);
  }, [fetchNotas, fetchAnexos, fetchIntegracao]);

  const handleCloseOSModal = useCallback(() => {
    setIsOSModalOpen(false);
    setSelectedOS(null);
    setNotas([]);
    setAnexos([]);
    setIntegracaoData(null);
    setNovaNota('');
  }, []);

  const handleStatusChange = useCallback(async (newStatus) => {
    if (!selectedOS) return;
    if (newStatus === 'Agendado') { setIsRescheduleModalOpen(true); return; }
    if (!window.confirm(`Alterar status para "${newStatus}"?`)) return;
    try {
      await api.put(`/api/ordens/${selectedOS.id}/status`, { status: newStatus }, getAuthConfig());
      if (newStatus === 'Concluído' || newStatus === 'Finalizada')
        alert('Lançamento financeiro criado automaticamente! ✨');
      refreshData();
      if ((newStatus === 'Concluído' || newStatus === 'Finalizada') && integracaoData) {
        fetchIntegracao(selectedOS.id);
      } else {
        handleCloseOSModal();
      }
    } catch (e) { alert(e.response?.data?.error || 'Erro ao atualizar status.'); }
  }, [selectedOS, refreshData, handleCloseOSModal, getAuthConfig, integracaoData, fetchIntegracao]);

  const handleRescheduleSubmit = useCallback(async () => {
    if (!rescheduleDateTime) return alert('Selecione data e hora.');
    if (!selectedOS) return;
    try {
      await api.put(`/api/ordens/${selectedOS.id}/reagendar`,
        { novaDataHora: rescheduleDateTime }, getAuthConfig());
      alert(`OS #${selectedOS.id} reagendada!`);
      refreshData();
      setIsRescheduleModalOpen(false);
      setRescheduleDateTime('');
      handleCloseOSModal();
    } catch (e) { alert(e.response?.data?.error || 'Erro ao reagendar.'); }
  }, [rescheduleDateTime, selectedOS, refreshData, handleCloseOSModal, getAuthConfig]);

  const handleReatribuir = useCallback(async () => {
    if (!reatribuirMotoristaId) return alert('Selecione um motorista.');
    if (!reatribuirJustificativa.trim()) return alert('Informe a justificativa.');
    if (!selectedOS) return;

    const motoristaSelecionado = motoristas.find(m => m.id === reatribuirMotoristaId);

    if (!window.confirm(
      `Reatribuir OS #${selectedOS.id} para ${motoristaSelecionado?.nome || 'o motorista selecionado'}?\n\nO motorista receberá uma notificação push com o motivo informado.`
    )) return;

    setIsReatribuindo(true);
    try {
      await api.put(
        `/api/ordens/${selectedOS.id}/reatribuir`,
        {
          novo_motorista_id: reatribuirMotoristaId,
          justificativa: reatribuirJustificativa.trim(),
        },
        getAuthConfig()
      );
      alert('OS reatribuída com sucesso! O motorista foi notificado.');
      setIsReatribuirModalOpen(false);
      setReatribuirMotoristaId(null);
      setReatribuirJustificativa('');
      refreshData();
      fetchNotas(selectedOS.id);
    } catch (e) {
      alert(e.response?.data?.error || 'Erro ao reatribuir OS.');
    } finally {
      setIsReatribuindo(false);
    }
  }, [reatribuirMotoristaId, reatribuirJustificativa, selectedOS, motoristas, getAuthConfig, refreshData, fetchNotas]);

  const handleExcluirOS = useCallback(async (osId) => {
    if (!window.confirm('Excluir esta OS permanentemente? O lançamento financeiro associado também será removido.')) return;
    try {
      await api.delete(`/api/ordens/${osId}`, getAuthConfig());
      alert(`OS #${osId} excluída.`);
      refreshData();
    } catch (e) { alert(e.response?.data?.error || 'Erro ao excluir.'); }
  }, [refreshData, getAuthConfig]);

  const handleFaturarOS = useCallback(async () => {
    if (!selectedOS) return;
    if (!window.confirm(`Faturar OS? Valor R$ ${selectedOS.valor?.toFixed(2)} será registrado.`)) return;
    try {
      await api.post(`/api/ordens/${selectedOS.id}/faturar`, {}, getAuthConfig());
      alert('OS faturada com sucesso!');
      refreshData();
      handleCloseOSModal();
    } catch (e) { alert(e.response?.data?.error || 'Erro ao faturar.'); }
  }, [selectedOS, refreshData, handleCloseOSModal, getAuthConfig]);

  const handleFileChange = useCallback(async (e) => {
    const file = e.target.files?.[0];
    if (!file || !selectedOS) return;
    try {
      const token = localStorage.getItem('token');
      const fd = new FormData();
      fd.append('autor', user?.nome || 'Usuário');
      fd.append('file', file);
      await api.post(`/api/ordens/${selectedOS.id}/anexos`, fd, {
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' }
      });
      alert('Anexo enviado!');
      fetchNotas(selectedOS.id);
      fetchAnexos(selectedOS.id);
      e.target.value = '';
    } catch { alert('Erro ao enviar anexo.'); }
  }, [selectedOS, user, fetchNotas, fetchAnexos]);

  const handleFilterChange = useCallback((e) =>
    setFilterData(p => ({ ...p, [e.target.name]: e.target.value })), []);

  const handleAddNota = useCallback(async () => {
    if (!novaNota.trim() || !selectedOS) return;
    try {
      await api.post(`/api/ordens/${selectedOS.id}/notas`,
        { autor: user?.nome || 'Usuário', nota: novaNota }, getAuthConfig());
      setNovaNota('');
      fetchNotas(selectedOS.id);
    } catch { alert('Erro ao adicionar nota.'); }
  }, [novaNota, selectedOS, user, fetchNotas, getAuthConfig]);

  const handleReenviarRetorno = async () => {
    if (!selectedOS) return;
    setEnviandoRetorno(true);
    try {
      await api.post(`/api/ordens/${selectedOS.id}/reenviar-integracao`, {}, getAuthConfig());
      toast.success('Retorno reprocessado com sucesso!');
      fetchNotas(selectedOS.id);
      fetchIntegracao(selectedOS.id);
      refreshData();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Falha ao reenviar retorno.');
      fetchIntegracao(selectedOS.id);
    } finally {
      setEnviandoRetorno(false);
    }
  };

  // ── Memoizados ───────────────────────────────
  const monthOptions = useMemo(() => {
    const s = new Set();
    ordens.forEach(o => { const m = getYYYYMM(o.data_criacao); if (m) s.add(m); });
    return Array.from(s).sort((a, b) => b.localeCompare(a));
  }, [ordens]);

  const ordensFiltradas = useMemo(() => {
    let base = ordens;

    if (searchScope === 'tab') {
      const tabName = normalize(selectedTab);
      base = base.filter(os => {
        const s = normalize(os.status);
        if (tabName === 'concluido') return s === 'concluido' || s === 'finalizada';
        return s === tabName;
      });
    }

    if (monthFilter)
      base = base.filter(os => getYYYYMM(os.data_criacao) === monthFilter);

    if (searchQuery.trim()) {
      const q = normalize(searchQuery);
      base = base.filter(os => {
        const cn = normalize(clientes.find(c => c.id === os.cliente_id)?.nome);
        const mn = normalize(motoristas.find(m => m.id === os.motorista_id)?.nome);
        const date = os.data_criacao ? new Date(os.data_criacao).toLocaleDateString('pt-BR') : '';
        return (
          os.id.toString().includes(q) ||
          normalize(os.descricao).includes(q) ||
          normalize(os.local_atendimento).includes(q) ||
          cn.includes(q) || mn.includes(q) || date.includes(q) ||
          normalize(os.placa_veiculo).includes(q) ||
          (os.valor && os.valor.toString().includes(q))
        );
      });
    }

    if (filterData.motorista_id)
      base = base.filter(os => os.motorista_id === parseInt(filterData.motorista_id, 10));
    if (filterData.origem)
      base = base.filter(os => (os.origem || 'interno') === filterData.origem);
    if (filterData.data_criacao)
      base = base.filter(os => os.data_criacao && String(os.data_criacao).startsWith(filterData.data_criacao));
    if (filterData.data_resolucao)
      base = base.filter(os => os.data_resolucao && String(os.data_resolucao).startsWith(filterData.data_resolucao));

    return base;
  }, [ordens, selectedTab, searchQuery, searchScope, filterData, clientes, motoristas, monthFilter]);

  const counts = useMemo(() => statusOptions.reduce((acc, s) => {
    const tabName = normalize(s);
    acc[s] = tabName === 'concluido'
      ? ordens.filter(o => { const st = normalize(o.status); return st === 'concluido' || st === 'finalizada'; }).length
      : ordens.filter(o => normalize(o.status) === tabName).length;
    return acc;
  }, {}), [ordens, statusOptions]);

  const totalPages = useMemo(() =>
    Math.max(1, Math.ceil(ordensFiltradas.length / PAGE_SIZE)), [ordensFiltradas.length]);

  useEffect(() => { setPage(1); }, [selectedTab, searchQuery, monthFilter, filterData]);

  const ordensPaginadas = useMemo(() => {
    const s = (page - 1) * PAGE_SIZE;
    return ordensFiltradas.slice(s, s + PAGE_SIZE);
  }, [ordensFiltradas, page]);

  const hasActiveFilters =
    filterData.motorista_id || filterData.origem ||
    filterData.data_criacao || filterData.data_resolucao || monthFilter;

  // ─────────────────────────────────────────
  // RENDER
  // ─────────────────────────────────────────
  return (
    <div className="itsm-root">

      {/* ══ HEADER ══ */}
      <header className="itsm-header">
        <div className="itsm-header-nav">
          <button className="itsm-back" onClick={() => navigate(-1)} title="Voltar">
            <FaArrowLeft size={14} />
          </button>
          <div>
            <h1 className="itsm-title">Fila de Ordens de Serviço</h1>
            <p className="itsm-subtitle">Portal de atendimento · Guincho &amp; Assistência</p>
          </div>
        </div>

        <div className="itsm-header-actions">
          {/* Atualizar */}
          <button className="itsm-new-os" onClick={handleManualRefresh} title="Atualizar dados"
            style={{ background: '#f1f5f9', color: '#64748b', border: '1px solid #cbd5e1' }}>
            <FaSync size={12} className={isRefreshing ? 'spin' : ''} />
          </button>

          {/* ← NOVO: relatórios de OS fechadas */}
          <button
            className="itsm-new-os"
            onClick={() => setShowRelatorioModal(true)}
            title="Gerar relatório de OS fechadas"
            style={{ background: '#eff6ff', color: '#1d4ed8', border: '1px solid #bfdbfe', gap: 6, display: 'inline-flex', alignItems: 'center' }}
          >
            <FaFileExport size={12} /> Relatório
          </button>

          {/* ← NOVO: botão distribuição automática — apenas admin/admin_geral */}
          {isAdminUser && (
            <button
              className="itsm-new-os"
              onClick={() => setShowAutoDistModal(true)}
              title="Configurar distribuição automática de OS"
              style={{
                background:  '#fef3c7',
                color:       '#92400e',
                border:      '1px solid #fde68a',
                gap:         6,
                display:     'inline-flex',
                alignItems:  'center',
              }}
            >
              <FaBolt size={12} /> Auto
            </button>
          )}

          {/* Nova OS */}
          <button className="itsm-new-os" onClick={() => setIsCadastroModalOpen(true)}>
            <FaPlus size={12} /> Nova OS
          </button>
        </div>
      </header>

      {/* ══ STATS BAR ══ */}
      <div className="itsm-stats">
        {statusOptions.map(s => {
          const meta   = STATUS_META[s] || {};
          const active = selectedTab === s;
          return (
            <button key={s} className={`stat-card ${active ? 'active' : ''}`}
              style={active ? { borderColor: meta.color, '--sc': meta.color } : {}}
              onClick={() => setSelectedTab(s)}>
              <span className="stat-num" style={active ? { color: meta.color } : {}}>{counts[s] || 0}</span>
              <span className="stat-lbl">{s}</span>
            </button>
          );
        })}
      </div>

      {/* ══ CONTROLES ══ */}
      <div className="itsm-searchbar-wrapper">
        <div className="itsm-searchbar">
          <div className="sb-input-wrap">
            <svg className="sb-icon" viewBox="0 0 20 20" fill="none">
              <circle cx="8.5" cy="8.5" r="5.5" stroke="currentColor" strokeWidth="1.6"/>
              <path d="M13.5 13.5L17 17" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round"/>
            </svg>
            <input className="sb-input" type="text"
              placeholder={searchScope === 'global'
                ? 'Busca global — ID, cliente, placa, local, motorista...'
                : `Buscar em "${selectedTab}" — ID, cliente, local...`}
              value={searchQuery} onChange={e => setSearchQuery(e.target.value)} autoComplete="off" />
            {searchQuery && (
              <button className="sb-clear" onClick={() => setSearchQuery('')} title="Limpar">
                <svg viewBox="0 0 16 16" fill="currentColor" width="14" height="14">
                  <path d="M4 4l8 8M12 4l-8 8" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"/>
                </svg>
              </button>
            )}
          </div>
          <div className="sb-divider" />
          <div className="sb-scope">
            <button className={`sb-scope-btn ${searchScope === 'tab' ? 'active' : ''}`} onClick={() => setSearchScope('tab')}>Aba</button>
            <button className={`sb-scope-btn ${searchScope === 'global' ? 'active' : ''}`} onClick={() => setSearchScope('global')}>Global</button>
          </div>
          <div className="sb-divider" />
          <select className="sb-month" value={monthFilter} onChange={e => setMonthFilter(e.target.value)} title="Filtrar por mês">
            <option value="">Todos os meses</option>
            {monthOptions.map(m => <option key={m} value={m}>{formatMonthLabel(m)}</option>)}
          </select>
          <button className={`sb-filter-btn ${hasActiveFilters ? 'active' : ''}`} onClick={() => setIsFilterModalOpen(true)}>
            <svg viewBox="0 0 20 20" fill="none" width="14" height="14">
              <path d="M3 5h14M6 10h8M9 15h2" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"/>
            </svg>
            Filtros
            {hasActiveFilters && <span className="sb-filter-dot" />}
          </button>
        </div>

        {hasActiveFilters && (
          <div className="sb-chips">
            {monthFilter && (
              <span className="sb-chip" style={monthFilter === getCurrentMonth() ? { background: '#f0fdf4', borderColor: '#bbf7d0' } : {}}>
                🗓 {monthFilter === getCurrentMonth() ? `Mês atual (${formatMonthLabel(monthFilter)})` : formatMonthLabel(monthFilter)}
                <button onClick={() => setMonthFilter('')}>✕</button>
              </span>
            )}
            {filterData.motorista_id && (
              <span className="sb-chip">
                🚛 {motoristas.find(m => m.id === parseInt(filterData.motorista_id, 10))?.nome || 'Motorista'}
                <button onClick={() => setFilterData(p => ({ ...p, motorista_id: '' }))}>✕</button>
              </span>
            )}
            {filterData.origem && (
              <span className="sb-chip">
                {filterData.origem === 'seguradora' ? '🔵' : filterData.origem === 'app' ? '🟢' : '🟠'} {filterData.origem}
                <button onClick={() => setFilterData(p => ({ ...p, origem: '' }))}>✕</button>
              </span>
            )}
            {filterData.data_criacao && (
              <span className="sb-chip">
                📅 Criado: {new Date(filterData.data_criacao + 'T12:00:00').toLocaleDateString('pt-BR')}
                <button onClick={() => setFilterData(p => ({ ...p, data_criacao: '' }))}>✕</button>
              </span>
            )}
            {filterData.data_resolucao && (
              <span className="sb-chip">
                📅 Resolvido: {new Date(filterData.data_resolucao + 'T12:00:00').toLocaleDateString('pt-BR')}
                <button onClick={() => setFilterData(p => ({ ...p, data_resolucao: '' }))}>✕</button>
              </span>
            )}
            <button className="sb-chip-clear-all"
              onClick={() => {
                setFilterData({ motorista_id: '', origem: '', data_criacao: '', data_resolucao: '' });
                setMonthFilter('');
              }}>
              Limpar tudo
            </button>
          </div>
        )}
      </div>

      {/* ══ LISTA ══ */}
      <main className="itsm-main">
        <div className="itsm-list-header">
          <span className="list-tab-label">
            {searchScope === 'global' ? 'Busca global' : selectedTab}
            {monthFilter && (
              <span style={{ marginLeft: 8, fontSize: '0.72rem', color: '#64748b', fontWeight: 600 }}>
                · {formatMonthLabel(monthFilter)}
              </span>
            )}
          </span>
          <span className="list-count">{ordensFiltradas.length} ordem{ordensFiltradas.length !== 1 ? 's' : ''}</span>
        </div>

        {ordensFiltradas.length > PAGE_SIZE && (
          <div className="pagination">
            <button className="pag-btn" onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1}>‹</button>
            <span>{page} / {totalPages}</span>
            <button className="pag-btn" onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page === totalPages}>›</button>
          </div>
        )}

        <div className="os-list">
          {ordensPaginadas.length > 0 ? (
            ordensPaginadas.map(os => (
              <OSCard key={os.id} os={os} clientes={clientes} motoristas={motoristas}
                user={user} onOpen={handleOpenOSModal} onDelete={handleExcluirOS} />
            ))
          ) : (
            <div className="list-empty">
              <span>📭</span>
              <p>
                {monthFilter
                  ? `Nenhuma OS com status "${selectedTab}" em ${formatMonthLabel(monthFilter)}`
                  : `Nenhuma ordem de serviço ${selectedTab === 'Na Fila' ? 'na fila' : `com status "${selectedTab}"`}`}
              </p>
              {monthFilter && (
                <button className="list-empty-cta" onClick={() => setMonthFilter('')}
                  style={{ background: '#64748b', marginTop: 8 }}>
                  Ver todos os períodos
                </button>
              )}
              {selectedTab === 'Na Fila' && !monthFilter && (
                <button className="list-empty-cta" onClick={() => setIsCadastroModalOpen(true)}>
                  <FaPlus size={12} /> Criar primeira OS
                </button>
              )}
            </div>
          )}
        </div>

        {ordensFiltradas.length > PAGE_SIZE && (
          <div className="pagination">
            <button className="pag-btn" onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1}>‹</button>
            <span>{page} / {totalPages}</span>
            <button className="pag-btn" onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page === totalPages}>›</button>
          </div>
        )}
      </main>

      {/* ══ MODAL OS ══ */}
      {isOSModalOpen && selectedOS && (() => {
        const clienteNome   = clientes.find(c => c.id === selectedOS.cliente_id)?.nome || 'N/A';
        const motoristaNome = motoristas.find(m => m.id === selectedOS.motorista_id)?.nome || 'N/A';
        const veiculoPlaca  = veiculos.find(v => v.id === selectedOS.veiculo_id)?.placa || 'N/A';
        const safeStatus    = selectedOS.status?.trim() || 'Na Fila';
        const isFinalizado  = ['Concluído', 'Finalizada', 'Cancelado', 'Lançamento Excluído'].includes(safeStatus);
        const isCompleted   = safeStatus === 'Concluído' || safeStatus === 'Finalizada';
        const meta          = STATUS_META[safeStatus] || STATUS_META['Na Fila'];
        const origem        = selectedOS.origem || (safeStatus === 'Finalizada' ? 'app' : 'interno');
        const videos        = (anexos || []).filter(a => (a.mime_type || '').startsWith('video/'));
        const temIntegracao = origem === 'seguradora' || integracaoData?.seguradora;

        return (
          <div className="modal-overlay" onClick={handleCloseOSModal}>
            <div className="modal-os" onClick={e => e.stopPropagation()}>
              <div className="modal-os-header" style={{ borderBottom: `3px solid ${meta.color}` }}>
                <div className="modal-os-title">
                  <div className="modal-os-id">
                    <span style={{ color: meta.color }}>OS #{selectedOS.id}</span>
                    <OrigemBadge origem={origem} />
                    <StatusBadge status={safeStatus} />
                  </div>
                  <div className="modal-os-meta">
                    <span>📅 {selectedOS.data_criacao ? new Date(selectedOS.data_criacao).toLocaleString('pt-BR') : '--'}</span>
                    <span>💰 R$ {selectedOS.valor?.toFixed(2) || '0.00'}</span>
                    {selectedOS.faturada_em && <span className="faturada-badge">✅ Faturada</span>}
                  </div>
                </div>
                <button className="modal-close-x" onClick={handleCloseOSModal}>✕</button>
              </div>

              <div className="modal-os-body">
                <div className="modal-left">
                  <div className="info-grid">
                    <div className="info-item"><span className="info-key">Cliente</span><span className="info-val">👤 {clienteNome}</span></div>
                    <div className="info-item"><span className="info-key">Motorista</span><span className="info-val">🚛 {motoristaNome}</span></div>
                    <div className="info-item"><span className="info-key">Veículo</span><span className="info-val">🚗 {veiculoPlaca}</span></div>
                    <div className="info-item info-full"><span className="info-key">Local</span><span className="info-val">📍 {selectedOS.local_atendimento}</span></div>
                    <div className="info-item info-full"><span className="info-key">Descrição</span><span className="info-val">{selectedOS.descricao}</span></div>
                  </div>

                  {temIntegracao && (
                    <div className="integration-block" style={{ marginTop: '16px', padding: '14px', background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: '8px' }}>
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '10px' }}>
                        <h4 style={{ margin: 0, display: 'flex', alignItems: 'center', gap: '6px', color: '#0f172a', fontSize: '0.85rem' }}>
                          <MdOutlineBusiness size={16} color="#3b82f6" /> Detalhes da Integração
                        </h4>
                        {loadingIntegracao && <FaSync className="spin" size={12} color="#64748b" />}
                      </div>
                      {integracaoData ? (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', fontSize: '0.8rem' }}>
                          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}>
                            <div>
                              <span style={{ color: '#64748b', display: 'block', marginBottom: '2px' }}>Seguradora</span>
                              <strong style={{ color: '#1e293b' }}>{integracaoData.seguradora.nome}</strong>
                            </div>
                            <div>
                              <span style={{ color: '#64748b', display: 'block', marginBottom: '2px' }}>Protocolo</span>
                              <strong style={{ color: '#1e293b' }}>{integracaoData.integracao.protocolo_externo || integracaoData.os.protocolo_externo || '—'}</strong>
                            </div>
                          </div>
                          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px', alignItems: 'center' }}>
                            <div>
                              <span style={{ color: '#64748b', display: 'block', marginBottom: '2px' }}>Status Retorno</span>
                              <IntegracaoStatusBadge statusIntegracao={integracaoData.integracao.status_integracao} />
                            </div>
                            <div style={{ textAlign: 'right' }}>
                              {isAdminUser && ['retorno_erro', 'retorno_pendente', 'retorno_enviado'].includes(integracaoData.integracao.status_integracao) && (
                                <button onClick={handleReenviarRetorno} disabled={enviandoRetorno}
                                  style={{ background: '#ffffff', border: '1px solid #cbd5e1', padding: '4px 10px', borderRadius: '6px', fontSize: '0.75rem', cursor: enviandoRetorno ? 'not-allowed' : 'pointer', display: 'inline-flex', alignItems: 'center', gap: '4px', color: '#475569', opacity: enviandoRetorno ? 0.7 : 1 }}>
                                  <FaRedo size={10} className={enviandoRetorno ? 'spin' : ''} />
                                  {enviandoRetorno ? 'Enviando...' : 'Reenviar Retorno'}
                                </button>
                              )}
                            </div>
                          </div>
                          {integracaoData.integracao.status_integracao === 'retorno_erro' && integracaoData.integracao.ultimo_erro && (
                            <div style={{ marginTop: '4px', padding: '8px', background: '#fef2f2', border: '1px dashed #fca5a5', borderRadius: '6px', color: '#b91c1c', display: 'flex', gap: '6px', alignItems: 'flex-start' }}>
                              <FaExclamationTriangle size={12} style={{ marginTop: '2px', flexShrink: 0 }} />
                              <span style={{ wordBreak: 'break-word', lineHeight: 1.4 }}>{integracaoData.integracao.ultimo_erro}</span>
                            </div>
                          )}
                        </div>
                      ) : (
                        <div style={{ color: '#94a3b8', fontSize: '0.8rem', textAlign: 'center', padding: '10px' }}>
                          Dados da integração não disponíveis no momento.
                        </div>
                      )}
                    </div>
                  )}

                  {!isFinalizado && <TimerDisplay status={safeStatus} data_criacao={selectedOS.data_criacao} />}

                  {isCompleted && (
                    <div className="evidence-section">
                      <div className="evidence-header">
                        <h4>📸 Evidências fotográficas</h4>
                        <ChecklistDisplay anexos={anexos} />
                      </div>
                      {loadingAnexos ? (
                        <div className="loading-photos"><FaSync className="spin" size={20} /><span>Carregando evidências...</span></div>
                      ) : <PhotoGallery anexos={anexos} />}
                      {videos.length > 0 && (
                        <div className="video-list">
                          <p className="video-list-title">🎥 Vídeos ({videos.length})</p>
                          {videos.map(v => (
                            <a key={v.id} href={v.web_view_link} target="_blank" rel="noreferrer" className="video-link">
                              <FaExternalLinkAlt size={11} /> {v.nome_arquivo}
                              {v.tamanho ? ` · ${(v.tamanho / 1024 / 1024).toFixed(1)} MB` : ''}
                            </a>
                          ))}
                        </div>
                      )}
                    </div>
                  )}

                  {!isCompleted && !loadingAnexos && anexos.length > 0 && (
                    <div className="active-attachments">
                      <h4>📎 Anexos</h4>
                      {anexos.map(a => (
                        <a key={a.id} href={a.web_view_link} target="_blank" rel="noreferrer" className="attachment-row">
                          {(a.mime_type || '').startsWith('image/') ? '🖼️' : (a.mime_type || '').startsWith('video/') ? '🎥' : '📄'}{' '}
                          {a.nome_arquivo}
                          {a.tamanho ? <span className="att-size">{(a.tamanho / 1024 / 1024).toFixed(1)} MB</span> : null}
                        </a>
                      ))}
                    </div>
                  )}
                </div>

                <div className="modal-right">
                  <h4>💬 Linha do tempo</h4>
                  <div className="chat-timeline">
                    {notas.length > 0 ? notas.map(n => (
                      <div key={n.id} className="note-bubble">
                        <div className="note-dot" />
                        <div className="note-content">
                          <span className="note-autor">{n.autor}</span>
                          <p className="note-text">{n.nota}</p>
                          <span className="note-time">{n.data_criacao ? new Date(n.data_criacao).toLocaleString('pt-BR') : ''}</span>
                        </div>
                      </div>
                    )) : (
                      <div className="notes-empty">
                        <span>💭</span>
                        <p>Nenhuma interação registrada</p>
                      </div>
                    )}
                  </div>

                  {!isFinalizado && (
                    <div className="note-composer">
                      <textarea
                        placeholder="Adicione uma nota ou atualização..."
                        value={novaNota}
                        onChange={e => setNovaNota(e.target.value)}
                        onKeyDown={e => { if (e.key === 'Enter' && e.ctrlKey) handleAddNota(); }}
                      />
                      <div className="note-composer-actions">
                        <input type="file" ref={fileInputRef} onChange={handleFileChange}
                          style={{ display: 'none' }} accept="image/*,video/*,application/pdf,.doc,.docx" />
                        <button className="composer-attach" onClick={() => fileInputRef.current.click()} title="Anexar arquivo">
                          <ClipIcon />
                        </button>
                        <button className="composer-send" onClick={handleAddNota} title="Enviar nota (Ctrl+Enter)">
                          Enviar
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              </div>

              <div className="modal-os-footer">
                <div className="action-row" style={{ display: 'flex', justifyContent: 'space-between', width: '100%', flexWrap: 'wrap', gap: 8 }}>
                  <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                    <button className="act-btn" onClick={handleCloseOSModal}
                      style={{ background: '#f1f5f9', color: '#64748b', border: '1px solid #cbd5e1' }}>
                      Fechar
                    </button>
                    {isAdminUser && !['Cancelado', 'Lançamento Excluído'].includes(safeStatus) && (
                      <button className="act-btn"
                        style={{ background: '#f5f3ff', color: '#7c3aed', border: '1px solid #ddd6fe' }}
                        onClick={() => {
                          setReatribuirMotoristaId(null);
                          setReatribuirJustificativa('');
                          setIsReatribuirModalOpen(true);
                        }}>
                        🔀 Reatribuir
                      </button>
                    )}
                  </div>
                  <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
                    {normalize(safeStatus) === 'na fila' && (
                      <>
                        <button className="act-btn act-start"    onClick={() => handleStatusChange('Em Andamento')}>🔄 Iniciar</button>
                        <button className="act-btn act-schedule" onClick={() => handleStatusChange('Agendado')}>📅 Agendar</button>
                        <button className="act-btn act-cancel"   onClick={() => handleStatusChange('Cancelado')}>✕ Cancelar</button>
                      </>
                    )}
                    {normalize(safeStatus) === 'agendado' && (
                      <>
                        <button className="act-btn act-start"  onClick={() => handleStatusChange('Em Andamento')}>🔄 Iniciar</button>
                        <button className="act-btn act-cancel" onClick={() => handleStatusChange('Cancelado')}>✕ Cancelar</button>
                      </>
                    )}
                    {normalize(safeStatus) === 'em andamento' && (
                      <>
                        <button className="act-btn act-schedule" onClick={() => handleStatusChange('Agendado')}>📅 Reagendar</button>
                        <button className="act-btn act-finish"   onClick={() => handleStatusChange('Concluído')}>✅ Finalizar</button>
                      </>
                    )}
                    {(normalize(safeStatus) === 'concluido' || normalize(safeStatus) === 'finalizada') && (
                      !selectedOS.faturada_em ? (
                        <button className="act-btn act-bill" onClick={handleFaturarOS}>💰 Faturar OS</button>
                      ) : (
                        <span className="act-done-msg" style={{ alignSelf: 'center' }}>✅ Faturada</span>
                      )
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        );
      })()}

      {/* ══ MODAL REATRIBUIÇÃO ══ */}
      {isReatribuirModalOpen && selectedOS && (
        <div className="modal-overlay" onClick={() => !isReatribuindo && setIsReatribuirModalOpen(false)}>
          <div className="modal-simple" onClick={e => e.stopPropagation()} style={{ maxWidth: 480, overflow: 'visible' }}>
            {!isReatribuindo && (
              <button className="modal-close-x sm" onClick={() => setIsReatribuirModalOpen(false)}>✕</button>
            )}
            <h3>🔀 Reatribuir OS #{selectedOS.id}</h3>
            <label className="simple-label">Motorista atual</label>
            <div style={{ padding: '10px 12px', background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 8, fontSize: '0.85rem', color: '#64748b', fontWeight: 600, marginBottom: 4 }}>
              🚛 {motoristas.find(m => m.id === selectedOS.motorista_id)?.nome || 'Nenhum motorista atribuído'}
            </div>
            <label className="simple-label" style={{ marginTop: 12 }}>Novo motorista *</label>
            <MotoristaSelect
              motoristas={motoristas}
              value={reatribuirMotoristaId}
              onChange={setReatribuirMotoristaId}
              currentMotoristaId={selectedOS.motorista_id}
              disabled={isReatribuindo}
            />
            <label className="simple-label" style={{ marginTop: 12 }}>
              Justificativa *{' '}
              <span style={{ fontWeight: 400, textTransform: 'none', fontSize: '0.7rem' }}>(ficará registrada na OS)</span>
            </label>
            <textarea
              className="simple-input"
              placeholder="Ex: Motorista original teve imprevisto, área fora de cobertura, solicitação do cliente..."
              value={reatribuirJustificativa}
              onChange={e => setReatribuirJustificativa(e.target.value)}
              disabled={isReatribuindo}
              rows={3}
              style={{ resize: 'vertical', minHeight: 80, fontFamily: 'inherit' }}
            />
            <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8, padding: '10px 12px', background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: 10, marginTop: 8, fontSize: '0.78rem', color: '#1e40af', fontWeight: 600 }}>
              <span style={{ fontSize: '1rem' }}>📲</span>
              <span>O novo motorista receberá uma notificação push com o motivo informado.</span>
            </div>
            <button
              className="simple-submit"
              onClick={handleReatribuir}
              disabled={isReatribuindo || !reatribuirMotoristaId || !reatribuirJustificativa.trim()}
              style={{ marginTop: 16, opacity: isReatribuindo || !reatribuirMotoristaId || !reatribuirJustificativa.trim() ? 0.6 : 1, cursor: isReatribuindo ? 'wait' : 'pointer' }}
            >
              {isReatribuindo ? '⏳ Reatribuindo...' : '✓ Confirmar reatribuição'}
            </button>
          </div>
        </div>
      )}

      {/* ══ MODAL REAGENDAMENTO ══ */}
      {isRescheduleModalOpen && (
        <div className="modal-overlay" onClick={() => setIsRescheduleModalOpen(false)}>
          <div className="modal-simple" onClick={e => e.stopPropagation()}>
            <button className="modal-close-x sm" onClick={() => setIsRescheduleModalOpen(false)}>✕</button>
            <h3>📅 Reagendar OS #{selectedOS?.id}</h3>
            <label className="simple-label">Nova data e hora</label>
            <input type="datetime-local" className="simple-input" value={rescheduleDateTime}
              onChange={e => setRescheduleDateTime(e.target.value)} />
            <button className="simple-submit" onClick={handleRescheduleSubmit}>Confirmar agendamento</button>
          </div>
        </div>
      )}

      {/* ══ MODAL FILTROS ══ */}
      {isFilterModalOpen && (
        <div className="modal-overlay" onClick={() => setIsFilterModalOpen(false)}>
          <div className="filter-modal" onClick={e => e.stopPropagation()}>
            <div className="filter-modal-header">
              <div>
                <h3>Filtros avançados</h3>
                <p>Refine os resultados da fila</p>
              </div>
              <button className="modal-close-x" onClick={() => setIsFilterModalOpen(false)}>✕</button>
            </div>
            <div className="filter-modal-body">
              <div className="filter-section">
                <label className="filter-label">Período</label>
                <select className="filter-select" value={monthFilter} onChange={e => setMonthFilter(e.target.value)}>
                  <option value="">Todos os meses</option>
                  {monthOptions.map(m => <option key={m} value={m}>{formatMonthLabel(m)}</option>)}
                </select>
              </div>
              <div className="filter-section">
                <label className="filter-label">Origem da OS</label>
                <div className="filter-origin-grid">
                  {[
                    { val: '',           icon: '🔘', label: 'Todas'      },
                    { val: 'interno',    icon: '🟠', label: 'Interno'    },
                    { val: 'seguradora', icon: '🔵', label: 'Seguradora' },
                    { val: 'app',        icon: '🟢', label: 'App Mobile' },
                  ].map(opt => (
                    <button key={opt.val}
                      className={`filter-origin-btn ${filterData.origem === opt.val ? 'selected' : ''}`}
                      onClick={() => setFilterData(p => ({ ...p, origem: opt.val }))}>
                      <span>{opt.icon}</span>
                      <span>{opt.label}</span>
                    </button>
                  ))}
                </div>
              </div>
              <div className="filter-section">
                <label className="filter-label">Motorista</label>
                <select className="filter-select" name="motorista_id" value={filterData.motorista_id} onChange={handleFilterChange}>
                  <option value="">Todos os motoristas</option>
                  {motoristas.map(m => <option key={m.id_raw} value={m.id}>{m.nome}</option>)}
                </select>
              </div>
              <div className="filter-dates">
                <div className="filter-section">
                  <label className="filter-label">Data de criação</label>
                  <input type="date" className="filter-input" name="data_criacao" value={filterData.data_criacao} onChange={handleFilterChange} />
                </div>
                <div className="filter-section">
                  <label className="filter-label">Data de resolução</label>
                  <input type="date" className="filter-input" name="data_resolucao" value={filterData.data_resolucao} onChange={handleFilterChange} />
                </div>
              </div>
            </div>
            <div className="filter-modal-footer">
              {hasActiveFilters && (
                <button className="filter-footer-reset" onClick={() => {
                  setFilterData({ motorista_id: '', origem: '', data_criacao: '', data_resolucao: '' });
                  setMonthFilter('');
                  setIsFilterModalOpen(false);
                }}>
                  Limpar filtros
                </button>
              )}
              <button className="filter-footer-apply" onClick={() => setIsFilterModalOpen(false)}>
                Ver {ordensFiltradas.length} resultado{ordensFiltradas.length !== 1 ? 's' : ''}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ══ MODAL DISTRIBUIÇÃO AUTOMÁTICA ══
          Renderizado APÓS todos os outros modais.
          Visível apenas para admin / admin_geral (o próprio modal valida internamente também).
      */}
      <ModalAutoDistribuicao
        isOpen={showAutoDistModal}
        onClose={() => setShowAutoDistModal(false)}
        user={user}
      />

      {/* ══ MODAL RELATÓRIO DE OS FECHADAS ══ */}
      <RelatorioOrdens
        isOpen={showRelatorioModal}
        onClose={() => setShowRelatorioModal(false)}
        ordens={ordens}
        clientes={clientes}
        motoristas={motoristas}
        user={user}
      />

      <CadastroOrdensModal
        isOpen={isCadastroModalOpen}
        onClose={() => setIsCadastroModalOpen(false)}
        onSuccess={refreshData}
        user={user}
      />
    </div>
  );
}